import React, { useEffect, useMemo, useState } from "react";
import {
  Search, FileText, Phone, Mail, Trash2, AlertCircle, Clock, Star, Award, Download, RefreshCw, GraduationCap, Briefcase,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const STATUS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "shortlisted", label: "Shortlisted" },
  { key: "rejected", label: "Rejected" },
  { key: "hired", label: "Hired" },
];
const STATUS_META = {
  new: { color: "#7A2021", bg: "#FDF2F2", icon: AlertCircle, label: "New" },
  shortlisted: { color: "#92400E", bg: "#FEF7E6", icon: Star, label: "Shortlisted" },
  rejected: { color: "#64748B", bg: "#F1F5F9", icon: Clock, label: "Rejected" },
  hired: { color: "#166534", bg: "#ECFDF5", icon: Award, label: "Hired" },
};
const POSITION_META = {
  internship: { label: "Internship", icon: GraduationCap, color: "#7A2021" },
  appointment: { label: "Appointment", icon: Briefcase, color: "#0A1128" },
};

export default function ApplicationsView({ onUnauthorized }) {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ new: 0, shortlisted: 0, rejected: 0, hired: 0, total: 0 });
  const [tab, setTab] = useState("all");
  const [posTab, setPosTab] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: apps }, { data: s }] = await Promise.all([
        api.get("/applications"),
        api.get("/applications/stats"),
      ]);
      setList(apps || []);
      setStats(s || stats);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      else toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    return list.filter((a) => {
      if (tab !== "all" && a.status !== tab) return false;
      if (posTab !== "all" && a.position_type !== posTab) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.phone.toLowerCase().includes(q) ||
        (a.college_or_firm || "").toLowerCase().includes(q) ||
        (a.cover_letter || "").toLowerCase().includes(q)
      );
    });
  }, [list, tab, posTab, query]);

  const updateStatus = async (id, statusVal) => {
    try {
      const { data } = await api.patch(`/applications/${id}`, { status: statusVal });
      setList((cur) => cur.map((x) => (x.id === id ? data : x)));
      if (selected?.id === id) setSelected(data);
      const prev = list.find((x) => x.id === id);
      if (prev) {
        setStats((s) => ({
          ...s,
          [prev.status]: Math.max(0, (s[prev.status] || 0) - 1),
          [statusVal]: (s[statusVal] || 0) + 1,
        }));
      }
      toast.success(`Marked as ${statusVal}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application and its résumé permanently?")) return;
    try {
      await api.delete(`/applications/${id}`);
      const prev = list.find((x) => x.id === id);
      setList((cur) => cur.filter((x) => x.id !== id));
      if (prev) {
        setStats((s) => ({
          ...s,
          [prev.status]: Math.max(0, (s[prev.status] || 0) - 1),
          total: Math.max(0, s.total - 1),
        }));
      }
      if (selected?.id === id) setSelected(null);
      toast.success("Application deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const resumeUrl = (id) => `${API}/applications/${id}/resume`;

  return (
    <div data-testid="applications-view">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.28em" }}>
            Careers
          </span>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-3" style={{ color: "#0A1128", fontWeight: 500 }}>
            Applications
          </h1>
        </div>
        <button onClick={load} className="btn-secondary" data-testid="admin-refresh-applications">
          <RefreshCw size={14} strokeWidth={1.6} /> Refresh
        </button>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px" style={{ backgroundColor: "#E2DCD0", border: "1px solid #E2DCD0" }}>
        <StatCard label="Total" value={stats.total} testid="app-stat-total" />
        <StatCard label="New" value={stats.new} accent="#7A2021" testid="app-stat-new" />
        <StatCard label="Shortlisted" value={stats.shortlisted} accent="#92400E" testid="app-stat-shortlisted" />
        <StatCard label="Hired" value={stats.hired} accent="#166534" testid="app-stat-hired" />
        <StatCard label="Rejected" value={stats.rejected} accent="#64748B" testid="app-stat-rejected" />
      </div>

      <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex flex-wrap gap-3">
          <Toggle items={STATUS} active={tab} onChange={setTab} testidPrefix="app-tab" />
          <Toggle
            items={[
              { key: "all", label: "All Positions" },
              { key: "internship", label: "Internships" },
              { key: "appointment", label: "Appointments" },
            ]}
            active={posTab}
            onChange={setPosTab}
            testidPrefix="app-pos"
          />
        </div>

        <div className="relative md:w-80" style={{ border: "1px solid #E2DCD0", backgroundColor: "#FFFFFF" }}>
          <Search size={16} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#64748B" }} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, college, note…"
            className="w-full pl-10 pr-4 py-3 bg-transparent outline-none text-sm"
            style={{ color: "#0A1128" }}
            data-testid="app-search"
          />
        </div>
      </div>

      <div className="mt-8" style={{ border: "1px solid #E2DCD0", backgroundColor: "#FFFFFF" }}>
        {loading ? (
          <EmptyState text="Loading applications…" />
        ) : filtered.length === 0 ? (
          <EmptyState text={list.length === 0 ? "No applications yet." : "No applications match your filters."} />
        ) : (
          <ul>
            {filtered.map((a) => {
              const PM = POSITION_META[a.position_type] || POSITION_META.internship;
              const PMIcon = PM.icon;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(a)}
                    className="w-full text-left px-6 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center transition-colors hover:bg-[#F9F6F0]"
                    style={{ borderBottom: "1px solid #E2DCD0" }}
                    data-testid={`application-row-${a.id}`}
                  >
                    <div className="md:col-span-3">
                      <div className="font-serif-display text-xl" style={{ color: "#0A1128" }}>{a.name}</div>
                      <div className="text-xs mt-1 flex items-center gap-2" style={{ color: "#64748B" }}>
                        <PMIcon size={12} strokeWidth={1.6} style={{ color: PM.color }} /> {PM.label}
                        {a.year ? ` · ${a.year}` : ""}
                      </div>
                    </div>
                    <div className="md:col-span-3 text-sm space-y-0.5" style={{ color: "#334155" }}>
                      <div className="flex items-center gap-2"><Mail size={13} strokeWidth={1.6} style={{ color: "#7A2021" }} /> <span className="truncate">{a.email}</span></div>
                      <div className="flex items-center gap-2"><Phone size={13} strokeWidth={1.6} style={{ color: "#7A2021" }} /> {a.phone}</div>
                    </div>
                    <div className="md:col-span-2 hidden md:block text-sm truncate" style={{ color: "#0A1128" }}>
                      {a.college_or_firm || "—"}
                    </div>
                    <div className="md:col-span-2 hidden md:block text-xs" style={{ color: "#64748B" }}>
                      {formatDate(a.created_at)}
                    </div>
                    <div className="md:col-span-2 flex md:justify-end">
                      <StatusPill status={a.status} />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 flex" data-testid="application-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-xl h-full overflow-y-auto" style={{ backgroundColor: "#FFFFFF", borderLeft: "1px solid #E2DCD0" }}>
            <div className="p-8 flex items-start justify-between" style={{ borderBottom: "1px solid #E2DCD0" }}>
              <div>
                <span className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.24em" }}>
                  {POSITION_META[selected.position_type]?.label || selected.position_type}
                </span>
                <h3 className="font-serif-display text-3xl mt-2" style={{ color: "#0A1128", fontWeight: 500 }}>{selected.name}</h3>
                <div className="mt-1 text-xs" style={{ color: "#64748B" }}>Received {formatDate(selected.created_at, true)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-xs uppercase" style={{ color: "#64748B", letterSpacing: "0.2em" }} data-testid="app-drawer-close">
                Close
              </button>
            </div>

            <div className="p-8 space-y-6">
              <Detail label="Email"><a href={`mailto:${selected.email}`} className="hover:underline" style={{ color: "#0A1128" }}>{selected.email}</a></Detail>
              <Detail label="Phone"><a href={`tel:${selected.phone}`} className="hover:underline" style={{ color: "#0A1128" }}>{selected.phone}</a></Detail>
              {selected.college_or_firm && <Detail label={selected.position_type === "internship" ? "Law School / College" : "Most Recent Firm / Chamber"}>{selected.college_or_firm}</Detail>}
              {selected.year && <Detail label={selected.position_type === "internship" ? "Year of Study" : "Years at the Bar"}>{selected.year}</Detail>}
              <Detail label="Cover Note"><p className="whitespace-pre-wrap" style={{ color: "#334155", lineHeight: 1.7 }}>{selected.cover_letter}</p></Detail>

              <div className="p-4 flex items-center gap-3" style={{ border: "1px solid #C5A059", backgroundColor: "rgba(197,160,89,0.06)" }}>
                <FileText size={18} strokeWidth={1.5} style={{ color: "#7A2021" }} />
                <div className="flex-1 min-w-0 text-sm" style={{ color: "#0A1128" }}>
                  <div className="truncate"><strong style={{ fontWeight: 500 }}>{selected.resume_filename}</strong></div>
                  <div className="text-xs" style={{ color: "#64748B" }}>{(selected.resume_size / 1024).toFixed(0)} KB</div>
                </div>
                <a
                  href={resumeUrl(selected.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs uppercase"
                  style={{ color: "#7A2021", letterSpacing: "0.18em" }}
                  data-testid="app-resume-download"
                >
                  <Download size={14} strokeWidth={1.6} /> Download
                </a>
              </div>

              <div>
                <div className="field-label">Status</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.keys(STATUS_META).map((k) => (
                    <button
                      key={k}
                      onClick={() => updateStatus(selected.id, k)}
                      className="px-4 py-2 text-xs uppercase"
                      style={{
                        letterSpacing: "0.16em",
                        border: `1px solid ${STATUS_META[k].color}`,
                        backgroundColor: selected.status === k ? STATUS_META[k].color : "transparent",
                        color: selected.status === k ? "#FFFFFF" : STATUS_META[k].color,
                      }}
                      data-testid={`app-drawer-status-${k}`}
                    >
                      {STATUS_META[k].label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex items-center gap-3" style={{ borderTop: "1px solid #E2DCD0" }}>
                <a href={`mailto:${selected.email}`} className="btn-secondary"><Mail size={14} strokeWidth={1.6} /> Email</a>
                <a href={`tel:${selected.phone}`} className="btn-secondary"><Phone size={14} strokeWidth={1.6} /> Call</a>
                <button onClick={() => remove(selected.id)} className="ml-auto inline-flex items-center gap-2 text-xs uppercase" style={{ color: "#7A2021", letterSpacing: "0.18em" }} data-testid="app-drawer-delete">
                  <Trash2 size={14} strokeWidth={1.6} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ items, active, onChange, testidPrefix }) {
  return (
    <div className="flex" style={{ border: "1px solid #E2DCD0", backgroundColor: "#FFFFFF" }}>
      {items.map((s) => (
        <button
          key={s.key}
          type="button"
          onClick={() => onChange(s.key)}
          className="px-4 py-3 text-xs uppercase transition-colors"
          data-testid={`${testidPrefix}-${s.key}`}
          style={{
            letterSpacing: "0.16em",
            backgroundColor: active === s.key ? "#0A1128" : "transparent",
            color: active === s.key ? "#F9F6F0" : "#0A1128",
          }}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

function StatCard({ label, value, accent = "#0A1128", testid }) {
  return (
    <div className="p-6 md:p-8" style={{ backgroundColor: "#FFFFFF" }} data-testid={testid}>
      <div className="text-[10px] uppercase" style={{ color: "#64748B", letterSpacing: "0.24em" }}>{label}</div>
      <div className="font-serif-display text-4xl md:text-5xl mt-2" style={{ color: accent, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <span className="pill" style={{ color: meta.color, backgroundColor: meta.bg }}>
      <Icon size={12} strokeWidth={1.8} /> {meta.label}
    </span>
  );
}

function EmptyState({ text }) {
  return (
    <div className="p-16 text-center">
      <FileText size={28} strokeWidth={1.4} className="mx-auto" style={{ color: "#C5A059" }} />
      <p className="mt-4 text-sm" style={{ color: "#64748B" }}>{text}</p>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="field-label">{label}</div>
      <div className="text-base mt-1" style={{ color: "#0A1128" }}>{children}</div>
    </div>
  );
}

function formatDate(iso, withTime) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    if (!withTime) return date;
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
}
